import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FormCheck } from '@/components/custom/form/form-check';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/custom/loader';
import { PATHS } from '@/routes/paths';
import { extractApiError } from '@/services/errors';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import tiaTrilhaEscolha from '@/assets/images/tia/TIA-TRILHA-ESCOLHA.png';
import { cn } from '@/lib/utils';
import { CreatingTrilhaLoader } from './creating-trilha-loader';

const FALLBACK_ERROR = 'Não foi possível criar a trilha.';

// Balão conversacional da T.I.A (mesmo desenho do wizard de exercícios).
function TiaAsk({ children }) {
  return (
    <div className="flex items-end gap-3">
      <img
        src={tiaTrilhaEscolha}
        alt=""
        aria-hidden="true"
        className="pointer-events-none h-20 w-auto shrink-0 object-contain object-bottom"
      />
      <div className="relative mb-2 flex-1 rounded-2xl rounded-bl-none bg-white p-4 shadow-card">
        <span
          className="block text-xs font-extrabold"
          style={{ color: 'var(--color-secondary-dark)' }}
        >
          T.I.A
        </span>
        {children}
      </div>
    </div>
  );
}

// Fluxo em 2 passos (objetivo → matérias) que cria uma trilha personalizada a
// partir do template adaptive. Monte só enquanto aberto: o estado zera a cada
// abertura sem reset manual. No tema lúdico, o POST roda com a T.I.A "montando
// o caderno" (CreatingTrilhaLoader); `onCreated` avisa que a trilha já existe.
export function CreateTrilhaDialog({ learningPathId, onClose, onCreated }) {
  const client = useClient();
  const navigate = useNavigate();
  const playful = usePlayfulStudent();
  const [objectives, setObjectives] = useState(null); // null = carregando
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [objective, setObjective] = useState(null);
  const [step, setStep] = useState('objetivo'); // 'objetivo' | 'materias'
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Só no lúdico: null = escolhendo; { id: null } = POST em andamento; { id } = criada.
  const [creating, setCreating] = useState(null);
  const requestPending = creating !== null && creating.id === null;

  useEffect(() => {
    let mounted = true;
    client
      .fetch('learning_objectives')
      .then(response => {
        if (!mounted) return;
        if (response.data) setObjectives(response.data);
        else setLoadFailed(true);
      })
      .catch(() => mounted && setLoadFailed(true));
    return () => {
      mounted = false;
    };
  }, [client, loadAttempt]);

  const retryLoad = () => {
    setObjectives(null);
    setLoadFailed(false);
    setLoadAttempt(n => n + 1);
  };

  const componentes = objective?.componentes ?? [];
  const toggle = name => setSelected(prev => (prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]));

  // Na ordem do objetivo, não na ordem de clique.
  const selectedInOrder = componentes.filter(c => selected.includes(c));

  const submit = async () => {
    setSubmitting(true);
    setSubmitError('');
    if (playful) setCreating({ id: null });
    const payload = {
      'learning-path-id': learningPathId,
      'learning-objective-id': objective.id,
      componentes: selectedInOrder
    };
    let message;
    try {
      const response = await client.mutate('user_learning_paths', payload, { method: 'POST' });
      if (response?.data) {
        if (playful) {
          setCreating({ id: response.data.id, trailId: response.data['trail-id'] });
          onCreated?.();
        } else {
          navigate(PATHS.LEARNING_PATH.CHAPTER(response.data['trail-id'], response.data.id));
        }
        return;
      }
      // jsonapi-react resolve (não rejeita) com `{ errors }` no 422 e `{ error }` nos demais status.
      message = extractApiError(response?.error ?? response, FALLBACK_ERROR);
    } catch (err) {
      message = extractApiError(err, FALLBACK_ERROR);
    }
    toast.error(message);
    // Lúdico: o erro fecha o modal (fica só o toast).
    if (playful) {
      onClose();
      return;
    }
    setSubmitting(false);
    setSubmitError(message);
  };

  const hint = step === 'objetivo' ? 'Qual é o seu objetivo? A trilha segue a matriz dele.' : `Quais matérias de ${objective.name} entram na trilha?`;

  const renderObjetivos = () => {
    if (loadFailed) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <p className="m-0 text-sm text-neutral-500">Não foi possível carregar os objetivos.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={retryLoad}
          >
            Tentar de novo
          </Button>
        </div>
      );
    }
    if (objectives === null) {
      return (
        <div className="grid place-items-center py-8">
          <Loader />
        </div>
      );
    }
    if (objectives.length === 0) return <p className="m-0 py-6 text-center text-sm text-neutral-500">Nenhum objetivo publicado ainda</p>;

    return (
      <RadioGroup
        className="flex-col gap-2"
        value={objective ? String(objective.id) : ''}
        onValueChange={value => {
          setObjective(objectives.find(o => String(o.id) === value));
          setSelected([]);
        }}
      >
        {objectives.map(o => (
          <Label
            key={o.id}
            htmlFor={`objetivo-${o.id}`}
            className={cn('flex cursor-pointer items-center gap-3 border-2 border-neutral-lighter bg-white p-3 font-bold text-neutral-dark transition-colors has-[[data-state=checked]]:border-primary', playful ? 'rounded-2xl' : 'rounded-xl')}
          >
            <RadioGroupItem
              id={`objetivo-${o.id}`}
              value={String(o.id)}
            />
            {o.name}
          </Label>
        ))}
      </RadioGroup>
    );
  };

  const renderMaterias = () =>
    componentes.length === 0 ? (
      <p className="m-0 py-6 text-center text-sm text-neutral-500">Nenhuma matéria disponível para esse objetivo</p>
    ) : (
      <div className="flex flex-col gap-3">
        <div className="flex justify-end gap-4 text-xs font-bold text-primary">
          <button
            type="button"
            onClick={() => setSelected(componentes)}
          >
            Selecionar todas
          </button>
          <button
            type="button"
            onClick={() => setSelected([])}
          >
            Limpar
          </button>
        </div>
        <div className={playful ? 'flex flex-wrap gap-2' : 'grid gap-2 sm:grid-cols-2'}>
          {componentes.map(name => (
            <FormCheck
              key={name}
              label={name}
              checked={selected.includes(name)}
              onCheckedChange={() => toggle(name)}
              className={cn(playful && 'rounded-full border-2 border-neutral-lighter bg-white px-3 py-2')}
            />
          ))}
        </div>
      </div>
    );

  // Enquanto o POST não volta, o modal não fecha (sem X, Esc ou clique fora).
  const handleOpenChange = open => {
    if (!open && !requestPending) onClose();
  };

  if (creating) {
    return (
      <Dialog
        open
        onOpenChange={handleOpenChange}
      >
        <DialogContent
          size="sm"
          layout="flex"
          hideCloseButton={requestPending}
          className="w-[580px] gap-0 rounded-[18px] bg-white"
        >
          <DialogHeader>
            <DialogTitle>Criar trilha personalizada</DialogTitle>
            <DialogDescription className="sr-only">A T.I.A está montando a sua trilha.</DialogDescription>
          </DialogHeader>
          <CreatingTrilhaLoader
            objectiveName={objective.name}
            subjects={selectedInOrder}
            serverDone={creating.id !== null}
            onClose={onClose}
            onStart={() => navigate(PATHS.LEARNING_PATH.CHAPTER(creating.trailId, creating.id))}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        size="sm"
        layout="flex"
        className={cn('gap-4', playful && 'rounded-[24px] bg-[var(--color-bg-cream)]')}
      >
        <DialogHeader>
          <DialogTitle>Criar trilha personalizada</DialogTitle>
          {playful ? (
            <TiaAsk>
              <DialogDescription className="font-bold text-neutral-dark">{hint}</DialogDescription>
            </TiaAsk>
          ) : (
            <DialogDescription>{hint}</DialogDescription>
          )}
        </DialogHeader>

        {step === 'objetivo' ? renderObjetivos() : renderMaterias()}

        {submitError && (
          <p
            role="alert"
            className="m-0 text-sm font-semibold text-danger"
          >
            {submitError}
          </p>
        )}

        <DialogFooter className="gap-2">
          {step === 'objetivo' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={!objective}
                onClick={() => setStep('materias')}
              >
                Avançar
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => setStep('objetivo')}
              >
                Voltar
              </Button>
              <Button
                type="button"
                disabled={selected.length === 0 || submitting}
                onClick={submit}
              >
                {submitting ? 'Criando…' : 'Criar trilha'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
